import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { map, take, catchError, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { IGetPerspective, IDefaultUsersPerspective, IDefaultPerspectiveInfo } from 'app/services/graphqlApi.service';
import { IPerspectiveDashboard } from './../../../../services/graphqlApi.service';
import { RestAPIService } from 'app/services/rest-api.service';
import { getDefaultPerspectiveUser, getPerspectivesById, getPerspectivesDetailsByIds } from 'app/shared/constants/gql';
import { PGroupValue, TreeNode, TreeNodeTypes } from 'app/shared/tree';
import { IPerspectiveAggregation, Perspective, PerspectiveTreeNode } from 'app/perspectives/interfaces/interfaces';
import { parsePerspectiveApiResponse, parseUserGroupPerspective } from 'app/shared/data/perspective';
import { TREE_NODE_TYPE } from 'app/perspectives/constants';
import { IDashboardResponse, ISelectedTag, ISummaryTimeStamp } from 'app/shared/panels/chart/Models/chart-settings.model';
import { RestRequestType } from 'app/shared/enums';

@Injectable({
  providedIn: 'root'
})
export class DashboardSidebarService {
  readonly selectedPerspective = new BehaviorSubject<Perspective | null>(null);
  readonly selectedPerspective$ = this.selectedPerspective.asObservable();

  readonly _selectedAsset = new BehaviorSubject<PGroupValue>(null);
  readonly selectedAsset$ = this._selectedAsset.asObservable();

  readonly _selectedGroupDashboards = new BehaviorSubject<IPerspectiveDashboard[]>([]);
  readonly selectedGroupDashboards$ = this._selectedGroupDashboards.asObservable();

  readonly _selectedGroupAggregations = new BehaviorSubject<IPerspectiveAggregation[]>([]);
  readonly selectedGroupAggregations$ = this._selectedGroupAggregations.asObservable();

  readonly _selectedAssetTimeStamp = new BehaviorSubject<ISummaryTimeStamp>(null);
  readonly selectedAssetTimeStamp$ = this._selectedAssetTimeStamp.asObservable();

  readonly _allDashboards = new BehaviorSubject<IDashboardResponse[]>([]);
  readonly allDashboards$ = this._allDashboards.asObservable();

  readonly _selectedDashboard = new BehaviorSubject<string>('');
  readonly selectedDashboard$ = this._selectedDashboard.asObservable();

  readonly perspectives = new BehaviorSubject<Perspective[]>([]);
  readonly perspectives$ = this.perspectives.asObservable();

  readonly _defaultPerspectives = new BehaviorSubject<IDefaultPerspectiveInfo>(null);
  readonly defaultPerspectives$ = this._defaultPerspectives.asObservable();

  readonly _perspectiveAggregations = new BehaviorSubject<IPerspectiveAggregation[]>([]);
  readonly perspectiveAggregations$ = this._perspectiveAggregations.asObservable();

  // selected Linked Singular tags for live-data
  readonly _selectedLinkedTags = new BehaviorSubject<ISelectedTag[]>([]);
  readonly selectedLinkedTags$ = this._selectedLinkedTags.asObservable();

  readonly _allPerspectives = new BehaviorSubject<Perspective[]>([]);
  readonly allPerspectives$ = this._allPerspectives.asObservable();

  // set perspective as default
  readonly _setPerspectiveAsDefault = new BehaviorSubject<string>('');
  readonly setPerspectiveAsDefault$ = this._setPerspectiveAsDefault.asObservable();

  constructor(
    private readonly apiService: RestAPIService,
    private readonly toastr: ToastrService) { }

  getPerspectiveById(id: string) {
    return this.apiService.getGQLData({ query: getPerspectivesById.query, params: { id: id } }).pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) =>{
        const queryresponse: IGetPerspective[] = response.data?.intellija_perspectives;
        return (queryresponse || []).map(parsePerspectiveApiResponse)
      }));

  }

  setPerspectiveById(id: string) {
    this.getPerspectiveById(id).pipe(take(1)).subscribe(perspective => {
      this.selectedPerspective.next(perspective[0]);
    });
  }

  buildTreeWithLeafNode(perspective: Perspective) {
    let node: TreeNode | null = null;
    if (perspective?.perspectiveTree?.length > 0) {
      const rootNodeData = perspective.perspectiveTree.find(pt => !pt.parentId);
      if (rootNodeData) {
        node = this.buildParentNode(rootNodeData, TREE_NODE_TYPE.root);
        this.buildRecurringNodeWithLeafNode(node, perspective.perspectiveTree, rootNodeData);
      }
    }

    return node ? [node] : [];
  }

  private buildRecurringNodeWithLeafNode(node: TreeNode, treeNodes: PerspectiveTreeNode[], rootNodeData: PerspectiveTreeNode) {
    const childNodesData = treeNodes.filter(n => n.parentId === rootNodeData.id).sort((a, b) => b.nodeIndex - a.nodeIndex);
    if (childNodesData.length > 0) {
      for (let childData of childNodesData) {
        if (childData.type == TREE_NODE_TYPE.group) {
          const assets = treeNodes.filter(tn => tn.type === TREE_NODE_TYPE.leaf && tn.parentId === childData.id);
          node.AddChild(this.buildNode(childData, assets));
          const newChildNode = node.getChildWithLabelName(childData.name);

          this.buildRecurringNodeWithLeafNode(newChildNode, treeNodes, childData);
        } else {
          node.AddChild(this.buildNode(childData, []));
        }
      }
    } else {
      return;
    }
  }

  buildNode(nodeData: PerspectiveTreeNode, assets = []): TreeNode {
    return new TreeNode({
      label: nodeData.name,
      children: [],
      nodeType: TreeNodeTypes.NODE,
      expanded: true,
      data: {
        pGroupValue: {
          value: nodeData.name,
          id: nodeData.id,
          type: nodeData.type,
          parentId: nodeData.parentId,
          assets,
        },
      }
    });
  }

  private buildParentNode(nodeData: PerspectiveTreeNode, type: string = null) {
    return new TreeNode({
      label: nodeData?.name,
      children: [],
      nodeType: TreeNodeTypes.NODE,
      expanded: true,
      data: {
        pGroupValue: {
          value: nodeData?.name,
          id: nodeData?.id,
          parentId: nodeData?.parentId,
          type: type || nodeData?.type,
          perspectiveId: nodeData?.perspectiveId,
        },
      }
    });
  }

  //#region set and get all perspective and Default perspective

  /**
   * set perspective as default
   * @param perspectiveId perspective id
   */
  setDefaultPerspectiveById(perspectiveId: string) {
    // console.log('alarm final override:>> ', alarm);
    return this.apiService.post(`/perspectives/${perspectiveId}/makedefault`, RestRequestType.DEFAULT_PERSPECTIVE)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          // console.log('res: ', res);
          if (res.result.status === 'ok') {
            this.toastr.success('Set Default Perspective successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
   * get default perspectice by user name
   * @param userName user name
   */
  getDefaultPerspectiveByUserName(userName: String) {
    return this.apiService.getGQLData({ query: getDefaultPerspectiveUser.query, params: { userName } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) =>{
      const queryresponse: IDefaultUsersPerspective[] = response.data?.intellija_perspectives_users;
      return (queryresponse || []);
    }));

  }

  /**
   * get all perspectives details by perspective Ids
   * @param perspectiveIds perspective ids
   */
  getPerspectivesDetailsByIds(perspectiveIds: String[]) {
      return this.apiService.getGQLData({ query: getPerspectivesDetailsByIds.query, params: { perspectiveIds: perspectiveIds } }).pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => {
      const queryresponse: IGetPerspective[] = response.data?.intellija_perspectives;
        return (queryresponse || []).map(parseUserGroupPerspective)
      }));
  }

  /**
   * fetch all perspective for a logged-in user
   */
  getAllPerspectives() {
    return this.apiService.get(`perspectives`)
    .pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }),
    map(response => response?.data?.perspectives || []));
  }

  //#endregion

}
